import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { State } from 'src/app/app.reducer';
import { PaymentType } from 'src/app/core/enums/enums';
import { PassengerMco, WaiverData } from 'src/app/core/models/itinerary.model';
import { PaymentResponse } from 'src/app/core/models/PaymentResponse';
import { Step } from 'src/app/core/models/step.model';
import { ExchangeQuoteRequest } from 'src/app/core/models/voluntary-change.model';
import { ItineraryService } from '../../search-result/services/itinerary.service';
import { BookingActions, PaymentActions, ReissueActions } from '../actions';
import * as fromReducer from '../reducers/reissue.reducer';
import {
  CreditCard,
  PaymentForm,
  PaymentMethod,
  ReissueRequestStatus
} from 'src/app/core/models/reissue.model';

@Injectable()
export class ReissueService {
  constructor(private store: Store<State>, private itineraryService: ItineraryService) {}

  dispatchLoadSteps(paymentType: PaymentType): void {
    this.store.dispatch(ReissueActions.LoadSteps({ paymentType }));
  }

  dispatchPaymentFirst(paymentType: PaymentType, formData?: CreditCard): void {
    this.store.dispatch(PaymentActions.Payment({ paymentType, formData, isPaymentFirst: true }));
  }

  dispatchAutomaticBooking(paymentType: PaymentType, formData: CreditCard): void {
    this.store.dispatch(
      BookingActions.AutomaticBooking({ paymentType, formData, isPaymentFirst: false })
    );
  }

  dispatchManualBooking(paymentType: PaymentType, formData: CreditCard): void {
    this.store.dispatch(
      BookingActions.ManualBooking({ paymentType, formData, isPaymentFirst: false })
    );
  }

  dispatchLoadPaymentMethods(paymentMethods: PaymentMethod[]): void {
    this.store.dispatch(PaymentActions.LoadPaymentMethods({ paymentMethods }));
  }

  dispatchReissue(formOfPayment: PaymentForm, quote: ExchangeQuoteRequest): void {
    this.store.dispatch(ReissueActions.Reissue({ formOfPayment, quote }));
  }

  dispatchSetBookingType(isAutomatic: boolean, data?: WaiverData): void {
    this.store.dispatch(ReissueActions.SetBookingType({ isAutomatic, waiver: data }));
  }

  dispatchSaveRemarks(remarks: string[]): void {
    this.store.dispatch(ReissueActions.SaveRemarks({ remarks }));
  }

  dispatchCreateRemarks(remarks: string[]): void {
    this.store.dispatch(ReissueActions.CreateRemarks({ remarks }));
  }

  dispatchFinalize(): void {
    this.itineraryService.navigateToWithParams('/itinerary');
    this.store.dispatch(ReissueActions.Finalize());
  }

  dispatchOpenPassengerMco(passengerMco: PassengerMco): void {
    this.store.dispatch(ReissueActions.UpdatePassengersMco({ passengerMco }));
  }

  dispatchResetPaymentForm() {
    this.store.dispatch(PaymentActions.ResetPaymentForm());
  }

  clearReissue() {
    this.store.dispatch(ReissueActions.ReissueClear());
  }

  getSteps$(): Observable<Step[]> {
    return this.store.pipe(select(fromReducer.getSteps));
  }

  getIsLoading$(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getIsLoading));
  }

  getPaymentMethods$(): Observable<PaymentMethod[]> {
    return this.store.pipe(select(fromReducer.getPaymentMethods));
  }

  getRequestStatus$(): Observable<ReissueRequestStatus> {
    return this.store.pipe(select(fromReducer.getRequestStatus));
  }

  getIsRetryAction$(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getIsRetryAction));
  }

  getPaymentForm$(): Observable<PaymentForm> {
    return this.store.pipe(select(fromReducer.getPaymentForm));
  }

  getPaymentResult$(): Observable<PaymentResponse> {
    return this.store.pipe(select(fromReducer.getPaymentResult));
  }

  getQuote$(): Observable<ExchangeQuoteRequest> {
    return this.store.pipe(select(fromReducer.getQuote));
  }

  getIsAutomatic$(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getIsAutomatic));
  }

  getBookStatus$(): Observable<boolean> {
    return this.store.pipe(select(fromReducer.getBookStatus));
  }

  getWaiver$(): Observable<WaiverData> {
    return this.store.pipe(select(fromReducer.getWaiver));
  }

  getRemarks$(): Observable<string[]> {
    return this.store.pipe(select(fromReducer.getRemarks));
  }

  getPassengersWithMco$(): Observable<PassengerMco[]> {
    return this.store.pipe(select(fromReducer.getPassengersWithMco));
  }

  getRemarksRequired$(): Observable<boolean> {
    return this.store.select(fromReducer.getRemarksRequired);
  }
}
